// TimeSeriesChart.js
// Generic time series chart component using chart.js

// Imports
import React from 'react';
import PropTypes from 'prop-types';
import Chart from 'chart.js';
import { ValueType, ChartMode } from './chartEnums';

// Portuguese month abbreviations
const MONTHS_PT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

// Returns a CSS rgba() string for any valid CSS color, at the given opacity.
// Works with named colors ('blue'), hex ('#fff'), rgb/rgba(), hsl(), etc.
// Falls back to a regex-based parse in environments without Canvas support (e.g. jsdom).
function colorWithAlpha(color, alpha) {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, 1, 1);
    const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  // Fallback: parse rgb/rgba format (covers test environments)
  const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (match) return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${alpha})`;
  return color;
}

// Formats a Date as DD/MM/YYYY
function formatDate(d) {
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

// Returns the Monday (week start) of the ISO week containing the given date
export function getWeekStart(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

// Aggregates raw data points by calendar day, summing Y values for the same day.
// Returns an array of { date: Date, value: number }.
export function aggregateByDay(dataPoints) {
  const map = {};
  for (const { x, y } of dataPoints) {
    const d = new Date(x);
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    if (!map[key]) {
      map[key] = { date: d, value: 0 };
    }
    map[key].value += y;
  }
  return Object.values(map);
}

// Processes daily-aggregated points into chart-ready { labels, values } based on mode.
// `now` is optional and defaults to the current date (injectable for testing).
// `ytdYear` is optional and overrides the target year in YTD mode.
export function processData(dailyPoints, mode, now, ytdYear, globalYears = null) {
  const today = now || new Date();

  if (mode === ChartMode.YEARLY) {
    const byYear = {};
    for (const { date, value } of dailyPoints) {
      const year = String(date.getFullYear());
      byYear[year] = (byYear[year] || 0) + value;
    }
    const dataYears = Object.keys(byYear).map(Number).sort((a,b) => a-b);
    let minYear, maxYear;

    if (globalYears && globalYears.length > 0) {
      minYear = Number(globalYears[0]);
      maxYear = Number(globalYears[globalYears.length - 1]);
    } else if (dataYears.length > 0) {
      minYear = dataYears[0];
      maxYear = dataYears[dataYears.length - 1];
    } else {
      return { labels: [], values: [] };
    }

    const years = [];
    for (let y = minYear; y <= maxYear; y++) years.push(String(y));
    return { labels: years, values: years.map(y => byYear[y] || 0) };
  }

  if (mode === ChartMode.YTD || mode === ChartMode.YTD_ACCUMULATED) {
    const targetYear = ytdYear != null ? ytdYear : today.getFullYear();
    // For a past year show all 12 months; for the current year stop at the current month
    const maxMonth = targetYear === today.getFullYear() ? today.getMonth() : 11;
    const byMonth = {};
    for (let m = 0; m <= maxMonth; m++) byMonth[m] = 0;
    for (const { date, value } of dailyPoints) {
      const m = date.getMonth();
      if (date.getFullYear() === targetYear && m <= maxMonth) {
        byMonth[m] = (byMonth[m] || 0) + value;
      }
    }
    const months = Object.keys(byMonth)
      .map(Number)
      .sort((a, b) => a - b);
      
    const values = months.map(m => byMonth[m]);
    
    // If YTD_ACCUMULATED, accumulate values month-over-month
    if (mode === ChartMode.YTD_ACCUMULATED) {
      for (let i = 1; i < values.length; i++) {
        values[i] += values[i - 1];
      }
    }

    return {
      labels: months.map(m => `${MONTHS_PT[m]}/${targetYear}`),
      values: values,
    };
  }

  if (mode === ChartMode.WEEKLY) {
    const currentWeekStart = getWeekStart(today);
    const weeks = [];
    for (let i = 9; i >= 0; i--) {
      const wStart = new Date(currentWeekStart);
      wStart.setDate(wStart.getDate() - i * 7);
      const wEnd = new Date(wStart);
      wEnd.setDate(wEnd.getDate() + 6);
      weeks.push({ start: wStart, end: wEnd, value: 0 });
    }
    for (const { date, value } of dailyPoints) {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      for (const week of weeks) {
        if (d >= week.start && d <= week.end) {
          week.value += value;
          break;
        }
      }
    }
    return {
      labels: weeks.map(w => `De ${formatDate(w.start)} a ${formatDate(w.end)}`),
      values: weeks.map(w => w.value),
    };
  }

  return { labels: [], values: [] };
}

// Component Class
// Shows a time series chart with selectable display mode
class TimeSeriesChart extends React.Component {
  constructor(props) {
    super(props);
    this.canvasRef = React.createRef();
    this.containerRef = React.createRef();
    this.chart = null;
    this.resizeObserver = null;
    this.state = {
      mode: props.initialMode || ChartMode.YTD,
      ytdYear: null, // null = auto-resolve to last year with data
    };
  }

  // Returns all years that appear in any dataset's data points, sorted ascending
  getAvailableYears() {
    const yearsSet = new Set();
    for (const ds of this.props.datasets) {
      for (const { x } of (ds.dataPoints || [])) {
        yearsSet.add(new Date(x).getFullYear());
      }
    }
    return Array.from(yearsSet).sort((a, b) => a - b);
  }

  // Resolves the active year: explicit user selection, or the last year with any data
  getEffectiveYtdYear() {
    const { ytdYear } = this.state;
    if (ytdYear !== null) return ytdYear;
    const years = this.getAvailableYears();
    return years.length > 0 ? years[years.length - 1] : new Date().getFullYear();
  }

  // Builds chart-ready data for one dataset from its dataPoints and the current mode
  getSeriesData(dataPoints, globalYears) {
    const daily = aggregateByDay(dataPoints || []);
    const ytdYear = (this.state.mode === ChartMode.YTD_ACCUMULATED || this.state.mode === ChartMode.YTD) 
      ? this.getEffectiveYtdYear() 
      : null;
    return processData(daily, this.state.mode, undefined, ytdYear, globalYears);
  }

  // Creates (or recreates) the chart.js instance
  buildChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
    const canvas = this.canvasRef.current;
    if (!canvas) return;

    const { datasets, valueType } = this.props;

    const availableYears = this.getAvailableYears();
    // In Yearly mode, we need all datasets to span exactly the same years, including empty ones!
    const globalYears = this.state.mode === ChartMode.YEARLY ? availableYears : null;

    // All datasets share the same labels (time buckets); derive them from the first series
    const firstSeries = datasets.length > 0 ? this.getSeriesData(datasets[0].dataPoints, globalYears) : { labels: [], values: [] };
    const labels = firstSeries.labels;

    const chartDatasets = datasets.map((ds) => {
      const { values } = this.getSeriesData(ds.dataPoints, globalYears);
      const baseColor = ds.color || 'rgba(45, 114, 210, 1)';
      const fillColor = colorWithAlpha(baseColor, 0.15);
      return {
        label: ds.label,
        data: values,
        backgroundColor: fillColor,
        borderColor: baseColor,
        borderWidth: 2,
        pointBackgroundColor: baseColor,
        pointRadius: 4,
        fill: this.props.fillArea !== false, // default to true
        lineTension: 0.35,
      };
    });

    this.chart = new Chart(canvas, {
      type: 'line',
      data: { labels, datasets: chartDatasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: 'top',
        },
        tooltips: {
          callbacks: {
            label: valueType === ValueType.CURRENCY
              ? (tooltipItem, data) => {
                  const dsLabel = data.datasets[tooltipItem.datasetIndex].label;
                  return `${dsLabel}: R$ ${tooltipItem.yLabel.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                }
              : (tooltipItem, data) => {
                  const dsLabel = data.datasets[tooltipItem.datasetIndex].label;
                  return `${dsLabel}: ${tooltipItem.yLabel}`;
                },
          },
        },
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true,
              callback: valueType === ValueType.CURRENCY
                ? (v) => `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : (v) => v,
            },
          }],
        },
      },
    });
  }

  componentDidMount() {
    this.buildChart();
    
    // Setup ResizeObserver to detect when the container changes size
    // (e.g. when the sidebar expands/collapses or the window resizes)
    if (window.ResizeObserver) {
      this.resizeObserver = new window.ResizeObserver(() => {
        if (this.chart) {
          this.chart.resize();
        }
      });
      if (this.containerRef.current) {
        this.resizeObserver.observe(this.containerRef.current);
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.datasets !== this.props.datasets ||
      prevProps.valueType !== this.props.valueType ||
      prevState.mode !== this.state.mode ||
      prevState.ytdYear !== this.state.ytdYear
    ) {
      this.buildChart();
    }
  }

  componentWillUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  // Preserves ytdYear across mode switches
  handleModeChange = (e) => {
    const newMode = e.target.value;
    this.setState({ mode: newMode }, () => {
      if (this.props.onModeChange) this.props.onModeChange(newMode, this.state.ytdYear);
    });
  }

  handleYtdYearChange = (e) => {
    const newYear = Number(e.target.value);
    this.setState({ ytdYear: newYear }, () => {
      if (this.props.onModeChange) this.props.onModeChange(this.state.mode, newYear);
    });
  }

  // Renders Component
  render() {
    const { mode } = this.state;
    const isYearDependent = mode === ChartMode.YTD_ACCUMULATED || mode === ChartMode.YTD;
    const availableYears = isYearDependent ? this.getAvailableYears() : [];
    const effectiveYtdYear = isYearDependent ? this.getEffectiveYtdYear() : null;
    return (
      <div style={{ width: '100%' }}>
        <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div className="pt-select">
            <select id="chart-mode-select" value={mode} onChange={this.handleModeChange}>
              <option value={ChartMode.YEARLY}>Anual</option>
              <option value={ChartMode.YTD}>Mensal</option>
              <option value={ChartMode.YTD_ACCUMULATED}>Mensal Acumulado no Ano</option>
              <option value={ChartMode.WEEKLY}>Semanal (últimas 10 semanas)</option>
            </select>
          </div>
          {isYearDependent && availableYears.length > 0 && (
            <div className="pt-select">
              <select
                id="chart-ytd-year-select"
                value={effectiveYtdYear}
                onChange={this.handleYtdYearChange}
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="time-series-chart__container" ref={this.containerRef}>
          <canvas ref={this.canvasRef} />
        </div>
      </div>
    );
  }
}

// Required Properties
TimeSeriesChart.propTypes = {
  datasets: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      dataPoints: PropTypes.arrayOf(
        PropTypes.shape({ x: PropTypes.any, y: PropTypes.number })
      ).isRequired,
      color: PropTypes.string,
    })
  ).isRequired,
  valueType: PropTypes.oneOf(Object.values(ValueType)).isRequired,
  initialMode: PropTypes.oneOf(Object.values(ChartMode)),
  onModeChange: PropTypes.func,
  fillArea: PropTypes.bool,
};

// Exports
export default TimeSeriesChart;



// WEBPACK FOOTER //
// ./src/components/ChartView/TimeSeriesChart.js